import { Filter, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { ColumnFilterConfig } from './types'
import type { FilterValue } from './state'

interface HeaderFilterButtonProps {
  config: ColumnFilterConfig
  value: FilterValue | undefined
  onChange: (next: FilterValue) => void
  onClear: () => void
}

export function HeaderFilterButton({ config, value, onChange, onClear }: HeaderFilterButtonProps) {
  const active = isActive(value)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'rounded p-0.5 transition-colors',
            active ? 'text-sky-600 hover:text-sky-700' : 'text-muted-foreground/50 hover:text-foreground',
          )}
          onClick={(e) => e.stopPropagation()}
          aria-label="Filtruj"
        >
          <Filter className="size-3.5" fill={active ? 'currentColor' : 'none'} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        <FilterEditor config={config} value={value} onChange={onChange} />
        {active && (
          <button
            type="button"
            onClick={onClear}
            className="mt-3 flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" /> Wyczyść filtr
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}

function FilterEditor({
  config,
  value,
  onChange,
}: {
  config: ColumnFilterConfig
  value: FilterValue | undefined
  onChange: (next: FilterValue) => void
}) {
  if (config.kind === 'text') {
    const v = value?.kind === 'text' ? value.value : ''
    return (
      <Input
        autoFocus
        placeholder={config.placeholder ?? 'Szukaj...'}
        value={v}
        onChange={(e) => onChange({ kind: 'text', value: e.target.value })}
      />
    )
  }
  if (config.kind === 'select') {
    const selected = value?.kind === 'select' ? value.values : []
    const multi = config.multi ?? false
    const toggle = (v: string) => {
      if (!multi) {
        onChange({ kind: 'select', values: selected.includes(v) ? [] : [v] })
        return
      }
      onChange({
        kind: 'select',
        values: selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v],
      })
    }
    return (
      <ul className="max-h-64 overflow-y-auto -mx-1">
        {config.options.map((opt) => (
          <li key={opt.value}>
            <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/40 cursor-pointer">
              <input
                type={multi ? 'checkbox' : 'radio'}
                className="size-4 accent-primary"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          </li>
        ))}
      </ul>
    )
  }
  if (config.kind === 'dateRange') {
    const from = value?.kind === 'dateRange' ? value.from : null
    const to = value?.kind === 'dateRange' ? value.to : null
    return (
      <div className="flex flex-col gap-2 text-sm">
        <label className="grid gap-1">
          <span className="text-xs text-muted-foreground">Od</span>
          <Input
            type="date"
            value={from ?? ''}
            onChange={(e) => onChange({ kind: 'dateRange', from: e.target.value || null, to })}
          />
        </label>
        <label className="grid gap-1">
          <span className="text-xs text-muted-foreground">Do</span>
          <Input
            type="date"
            value={to ?? ''}
            onChange={(e) => onChange({ kind: 'dateRange', from, to: e.target.value || null })}
          />
        </label>
      </div>
    )
  }
  // bool
  const cur = value?.kind === 'bool' ? value.value : null
  return (
    <div className="flex flex-col gap-1 text-sm">
      {[
        { v: null, label: 'Wszystkie' },
        { v: true, label: config.trueLabel ?? 'Tak' },
        { v: false, label: config.falseLabel ?? 'Nie' },
      ].map((opt) => (
        <label key={String(opt.v)} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/40 cursor-pointer">
          <input
            type="radio"
            className="size-4 accent-primary"
            checked={cur === opt.v}
            onChange={() => onChange({ kind: 'bool', value: opt.v as boolean | null })}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  )
}

function isActive(value: FilterValue | undefined): boolean {
  if (!value) return false
  switch (value.kind) {
    case 'text':
      return value.value.trim() !== ''
    case 'select':
      return value.values.length > 0
    case 'dateRange':
      return value.from !== null || value.to !== null
    case 'bool':
      return value.value !== null
  }
}
